import React, { useState } from "react";
import * as yup from "yup";
import { useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import {
  LoadingIndicator,
  ToggleSwitchInput,
  SubmitButton,
  useFeatureFlagWithTenant
} from "elements";
import { Redirect } from "polyrouter";
import { useMicrosoftAuth } from "./useMicrosoftAuth";
import {
  DISMISS,
  Formik,
  FormikHelpers,
  useNotification,
  Form
} from "collections";
import {
  useTenantIntegrationsQuery,
  useDeleteTenantIntegrationMutation,
  useCreateTenantIntegrationMutation,
  IntegrationType,
  TenantIntegrationsResponse,
  MicrosoftScopes,
  TenantIntegrationInput
} from "generated/graphql";

const MicrosoftFormSchema = yup.object({
  allowAccess: yup.bool().required()
});
type MicrosoftForm = yup.InferType<typeof MicrosoftFormSchema>;

const IntegrateTenantSettings: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { Content } = Layout;
  const { data, fetching, error } = useTenantIntegrationsQuery({
    variables: {
      tenantId: id,
      integration: {
        integrationType: IntegrationType.Microsoft,
        detail: { scopes: [MicrosoftScopes.DirReadAll] }
      }
    },
    requestPolicy: "network-only"
  });

  const [isEnabled, , clientReady] = useFeatureFlagWithTenant(
    "INTEGRATION_AZURE_AD"
  );

  if (error) return null;

  if (clientReady && !isEnabled) return <Redirect to="/home" />;

  if (!clientReady || fetching) return <LoadingIndicator />;

  return (
    <Content>
      <div className="card title-card">{t("DIRECTORY_INTEGRATION_TITLE")}</div>
      <div className="mb-1 ml-1">
        <label>{t("INTEGRATIONS_MICROSOFT_SUBTITLE")}</label>
      </div>
      <IntegrateMicrosoftTenantSettings
        id={id}
        currentIntegration={data?.tenantIntegrations?.[0] ?? null}
      />
    </Content>
  );
};

const IntegrateMicrosoftTenantSettings: React.FC<{
  id: string;
  currentIntegration: TenantIntegrationsResponse | null;
}> = ({ id, currentIntegration }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [currentState, setCurrentState] = useState(currentIntegration !== null);
  const { getAccessToken } = useMicrosoftAuth();
  const [, deleteTenantIntegration] = useDeleteTenantIntegrationMutation();
  const [, createTenantIntegration] = useCreateTenantIntegrationMutation();

  const onSubmit = async (
    { allowAccess }: MicrosoftForm,
    { resetForm }: FormikHelpers<MicrosoftForm>
  ) => {
    let error;

    // if allowAccess radio is checked, we're creating a new integration which
    // requires access
    if (allowAccess) {
      const token = await getAccessToken();
      if (!token) {
        resetForm();
        return;
      }

      const integration: TenantIntegrationInput = {
        integrationType: IntegrationType.Microsoft,
        detail: {
          MicrosoftTenantId: token?.tenantId,
          scopes: [MicrosoftScopes.DirReadAll]
        }
      };
      error = (await createTenantIntegration({ tenantId: id, integration }))
        ?.error;
    } else {
      // we are deleting the existing integration
      const integration: TenantIntegrationInput = {
        integrationType: IntegrationType.Microsoft,
        detail: {
          MicrosoftTenantId: currentIntegration?.detail?.MicrosoftTenantId
        }
      };
      error = (await deleteTenantIntegration({ tenantId: id, integration }))
        ?.error;
    }

    // Reset form on error, otherwise update component state
    if (error) resetForm();
    else {
      setCurrentState(allowAccess);
      addNotification({
        type: "success",
        message: t(
          allowAccess === true
            ? "INTEGRATIONS_MICROSOFT_CREATED"
            : "INTEGRATIONS_MICROSOFT_DELETED"
        ),
        dismiss: DISMISS.DEFAULT
      });
    }
  };

  return (
    <div className="integration ph-2">
      <div className="mb-1">
        <p>{t("INTEGRATIONS_MICROSOFT_INFO")}</p>
      </div>
      <Formik
        name="microsoftForm"
        validationSchema={MicrosoftFormSchema}
        initialValues={{ allowAccess: currentState }}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, isSubmitting, dirty }) => (
          <Form>
            <div className="grid mb-1">
              <div className="col-2" />
              <div className="col-8">
                <label htmlFor="microsoftToggle">
                  {t("INTEGRATIONS_ALLOW_ACCESS")}
                </label>
                <ToggleSwitchInput
                  className="d-inline-block ml-4"
                  name="microsoftToggle"
                  data-testid="microsoftToggle"
                  checked={values.allowAccess}
                  disabled={isSubmitting}
                  onChange={() => {
                    setFieldValue("allowAccess", !values.allowAccess);
                  }}
                />
              </div>
            </div>
            <div>
              <SubmitButton
                className="btn btn-primary"
                isSubmitting={isSubmitting}
                testId="microsoftSubmit"
                disabled={!dirty}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default IntegrateTenantSettings;
